import React from 'react';
import { INVESTMENT_TOURS } from './investments';

// Guided-tour content for each module. Each step can highlight an element marked
// with data-tour="<name>" on the page; steps without a target show as a centred card.
// To add a tour for another module: add an entry here, tag the page's elements with
// data-tour attributes, and render <GuidedTour> + <TourButton> on that page.

// ---- Credit Cards helpers ----
// Prefer a card that currently has a bill (the best example); otherwise the first card.
const CARD_WITH_BILL = '[data-tour="cc-card"][data-has-bill="true"]';
const inCard = (name) => [`${CARD_WITH_BILL} [data-tour="${name}"]`, `[data-tour="${name}"]`];

// On phones cards are collapsed, hiding the bill section and buttons. Expand the
// example card first. Returns true if it changed the page (so the tour waits).
const expandExampleCard = () => {
  const card = document.querySelector(CARD_WITH_BILL) || document.querySelector('[data-tour="cc-card"]');
  const toggle = card?.querySelector('[aria-expanded="false"]');
  if (!toggle) return false;
  toggle.click();
  return true;
};

export const TOURS = {
  dashboard: [
    {
      title: 'Welcome to Mickles',
      body: (
        <>
          <p>Your <b>Dashboard</b> brings all your money together in one place: bank accounts, cash, credit cards, investments and loans.</p>
          <p>Take a quick tour to see what each part means. It only takes a minute.</p>
        </>
      ),
    },
    {
      target: '[data-tour="dashboard-quick"]',
      title: 'Quick Access',
      body: (
        <p>Shortcuts to every part of the app: Transactions, Wallets, Investments, Insurance, Loans, Credit Cards, the Finance Calculator, Utilities and your Profile. You can also use the menu.</p>
      ),
    },
    {
      target: '[data-tour="dashboard-accounts"]',
      title: 'Account Summary',
      body: (
        <ul>
          <li><b>Bank Accounts</b> and <b>Cash &amp; Digital</b>: the total in those wallets.</li>
          <li><b>Credit Used</b>: what you currently owe across your credit cards.</li>
          <li><b>Net Inhand</b>: bank + cash, minus what you owe on credit cards. This is the money that's truly yours to spend.</li>
        </ul>
      ),
    },
    {
      target: '[data-tour="dashboard-investments"]',
      title: 'Investments',
      body: (
        <p><b>Total Portfolio</b> is the total amount you've invested across stocks, mutual funds, deposits, gold and more. It's based on what you paid. Open <b>Investments</b> to see live values and profit or loss.</p>
      ),
    },
    {
      target: '[data-tour="dashboard-loans"]',
      title: 'Loans',
      body: (
        <p><b>Outstanding</b> is how much you still have to repay across all your loans, after the payments you've recorded.</p>
      ),
    },
    {
      target: '[data-tour="dashboard-networth"]',
      title: 'Net Worth',
      body: (
        <p>The big picture: <b>Investments + Net Inhand − Loans</b>. Watch this number grow as your mickles make a muckle.</p>
      ),
    },
    {
      target: '[data-tour="dashboard-cards"]',
      title: 'Credit card health',
      body: (
        <>
          <p>How many cards you have, how many have a balance (<b>Used</b>), and where this month's bills stand: <b>Bill Generated</b>, <b>Fully Paid</b>, <b>Unpaid</b> and <b>Partially Paid</b>.</p>
          <p>Bills are worked out from each card's billing and due dates, which you set in <b>Credit Cards</b>.</p>
          <p><b>Click any number</b> to open Credit Cards showing just those cards, for example only the <b>Unpaid</b> ones.</p>
        </>
      ),
    },
    {
      target: '[data-tour="dashboard-dues"]',
      title: 'Outstanding Payments',
      body: (
        <p>The total still to pay on your credit card bills. When everything's paid, you'll see <b>All bills paid!</b> Click it to see the cards that still need paying.</p>
      ),
    },
    {
      target: '[data-tour="dashboard-insurance"]',
      title: 'Insurance',
      body: (
        <p>Your active policies, total cover (life and health shown separately) and what you pay in premiums a year. Next to it, <b>Premiums Due</b> shows anything due in the next 30 days. Click either to open Insurance.</p>
      ),
    },
    {
      target: '[data-tour="dashboard-activity"]',
      title: 'Recent Activity',
      body: (
        <p>Your last 5 entries from everywhere: <b>transactions, transfers, loan payments, insurance premiums and FASTag</b>, newest first. Click one to open it where it was recorded.</p>
      ),
    },
    {
      title: "Let's get you set up",
      body: (
        <>
          <p>The Dashboard fills in as you add your data. The best place to start is <b>Wallets</b>: add your bank accounts, credit cards and cash.</p>
          <p>Replay this guide any time with the <b>Guide</b> button.</p>
        </>
      ),
      action: { label: 'Start with Wallets', to: '/wallets' },
    },
  ],

  'credit-cards': [
    {
      title: 'Welcome to Credit Cards',
      body: (
        <>
          <p>Keep every card under control: <b>limits</b>, <b>this month's bill</b>, <b>due dates</b>, <b>rewards</b> and <b>annual-fee waivers</b>.</p>
          <p>The cards here are your <b>Credit Card wallets</b>. To add a new card, add it in <b>Wallets</b> first.</p>
        </>
      ),
    },
    {
      target: '[data-tour="cc-summary"]',
      title: 'Your cards at a glance',
      body: (
        <ul>
          <li><b>Total Limit</b>: combined limit (cards sharing one limit are counted once).</li>
          <li><b>Used</b>: how much of your limit is currently in use (your outstanding balance).</li>
          <li><b>Bills Due</b>: what's still to pay on generated bills.</li>
          <li><b>Reward Points</b>: points across all cards.</li>
        </ul>
      ),
    },
    {
      target: '[data-tour="cc-filters"]',
      title: 'Find the card you need',
      body: (
        <p>Search by name, filter by bill status (<b>Unpaid</b>, <b>Overdue</b>, <b>Partially Paid</b>, <b>Fully Paid</b> and more), sort by limit, bill date or fee-waiver progress, and show archived cards.</p>
      ),
    },
    {
      target: [CARD_WITH_BILL, '[data-tour="cc-card"]'],
      title: 'Each card',
      body: (
        <>
          <p>The badges show whether a card is <b>Primary</b> or <b>Linked</b> (sharing a limit), and its <b>bill status</b>. A <b>red stripe</b> on the left means a bill needs paying.</p>
          <p>On a phone, <b>tap a card</b> to see its full details and buttons.</p>
        </>
      ),
    },
    {
      target: inCard('cc-edit'),
      prepare: expandExampleCard,
      title: 'Set up each card first',
      body: (
        <>
          <p>Open <b>Edit Details</b> and enter the card's <b>Billing Date</b> and <b>Due Date</b> (the day of the month each happens). Mickles needs them to work out your bill.</p>
          <p>Optionally add the <b>Anniversary Month</b>, <b>Annual Fee</b> and <b>Spend Waiver</b> to track fee-waiver progress. Every field in the form has a tip explaining it.</p>
        </>
      ),
    },
    {
      target: inCard('cc-bill'),
      prepare: expandExampleCard,
      title: 'Bill Payment Status',
      body: (
        <>
          <p>Your bill is any unpaid balance from earlier plus this billing cycle's spending, minus refunds. Payments made since the bill date count towards it.</p>
          <p>You'll see how much is paid, what's remaining and how many days are left until the due date.</p>
        </>
      ),
    },
    {
      target: inCard('cc-rewards'),
      prepare: expandExampleCard,
      title: 'Rewards and fee waiver',
      body: (
        <p>See the annual fee, your points and what they're worth, and how close you are to spending enough to <b>waive the annual fee</b> before your card anniversary.</p>
      ),
    },
    {
      target: inCard('cc-rewards-btn'),
      prepare: expandExampleCard,
      title: 'Update reward points',
      body: (
        <>
          <p>Open <b>Rewards</b> to record points you've earned or redeemed: type a positive number to add (e.g. <b>500</b>) or a negative number to subtract (e.g. <b>-2000</b>) and press <b>Update</b>.</p>
          <p>Set what <b>1 point is worth in ₹</b> to see the value of your points. Every change is listed in the rewards history.</p>
        </>
      ),
    },
    {
      target: inCard('cc-statements'),
      prepare: expandExampleCard,
      title: 'Statements',
      body: (
        <p>View the card's transactions for each billing period, just like a bank statement, and download them as <b>CSV</b>, <b>PDF</b> or <b>HTML</b>.</p>
      ),
    },
    {
      title: 'Paying your bill',
      body: (
        <>
          <p>When you pay a card bill, record it in <b>Transactions</b> as a <b>Transfer</b> from your bank account to the card.</p>
          <p>The card's bill status updates automatically to <b>Partially Paid</b> or <b>Fully Paid</b>. Replay this guide any time with the <b>Guide</b> button.</p>
        </>
      ),
      action: { label: 'Go to Transactions', to: '/transactions' },
    },
  ],

  loans: [
    {
      title: 'Welcome to Loans',
      body: (
        <>
          <p>Track every loan you're repaying: home, car, personal, education, gold and more.</p>
          <p>Mickles works out the interest, what you still owe, and when your next payment is due.</p>
        </>
      ),
    },
    {
      target: '[data-tour="loans-add"]',
      title: 'Add a loan',
      body: (
        <>
          <p>Enter a name, the lender, the loan type, the <b>Principal Amount</b> and the yearly <b>Interest Rate</b>. Then choose how you repay it:</p>
          <ul>
            <li><b>EMI Based</b>: enter the <b>Tenure</b> in months. The EMI and end date are calculated for you, and you can adjust the EMI to match your bank's figure.</li>
            <li><b>One Time Settlement</b>: repaid in one go. Enter the <b>Loan Due Date</b>.</li>
          </ul>
        </>
      ),
    },
    {
      target: '[data-tour="loans-filters"]',
      title: 'Find a loan',
      body: (
        <p>Search by name, filter by status (<b>Active</b>, <b>Overdue</b>, <b>Due Soon</b>, <b>Completed</b>) or loan type, and sort the list. Only active loans are shown at first.</p>
      ),
    },
    {
      target: '[data-tour="loans-status"]',
      title: 'Payment status',
      body: (
        <ul>
          <li><b>Overdue</b> (red): the payment date has passed. The card also gets a red border.</li>
          <li><b>Due in a few days</b> (orange): due within the next 5 days.</li>
          <li><b>Due in … days</b> (blue): coming up later.</li>
          <li><b>Completed</b> (green): fully repaid.</li>
        </ul>
      ),
    },
    {
      target: '[data-tour="loans-amounts"]',
      title: 'Principal & Interest',
      body: (
        <p>What you borrowed, the interest you'll pay over the whole loan, the total you'll repay, and how much of it you've paid so far.</p>
      ),
    },
    {
      target: '[data-tour="loans-payment-details"]',
      title: 'Payment Details',
      body: (
        <p>Your <b>EMI amount</b> and how many EMIs are made and remaining (or the due date for a one-time loan), plus the <b>Next Payment</b> date.</p>
      ),
    },
    {
      target: '[data-tour="loans-progress"]',
      title: 'Progress',
      body: (
        <p>How much of the loan you've cleared, split into <b>principal</b> and <b>interest</b> paid, and the amount <b>remaining</b>. Interest is charged first, so early payments are mostly interest.</p>
      ),
    },
    {
      target: '[data-tour="loans-pay"]',
      title: 'Record a payment',
      body: (
        <>
          <p>Each time you pay an EMI or part of a loan, open <b>Add Payment</b> and enter the amount and date. Use <b>+</b> to record several payments at once, for example a few missed months.</p>
          <p>Loan payments don't change your wallet balances. To show the money leaving your bank, also record it as an <b>Expense</b> in Transactions.</p>
        </>
      ),
    },
    {
      target: ['[data-tour="loans-advance"]', '[data-tour="loans-actions"]'],
      title: 'Pay extra (prepayment)',
      body: (
        <>
          <p>Paying more than your EMI? Use <b>Advance Payment</b> on an EMI loan and choose to:</p>
          <ul>
            <li><b>Reduce EMI Amount</b>: keep the same end date and pay less each month.</li>
            <li><b>Reduce Loan Tenure</b>: keep the same EMI and finish sooner, which usually saves more interest.</li>
          </ul>
          <p>You'll see the interest you save before you confirm.</p>
        </>
      ),
    },
    {
      target: '[data-tour="loans-actions"]',
      title: 'More options',
      body: (
        <ul>
          <li><b>History</b>: every payment recorded, where you can edit or delete one.</li>
          <li><b>Amortization</b> (EMI loans): month by month, how each EMI splits into principal and interest.</li>
          <li><b>Edit</b> or <b>Delete</b> the loan.</li>
        </ul>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Add your loans and record payments as you make them. The total you still owe appears on your <b>Dashboard</b> and is taken off your net worth. Replay this guide any time with the <b>Guide</b> button.</p>
      ),
      action: { label: 'Go to Dashboard', to: '/' },
    },
  ],

  utilities: [
    {
      title: 'Welcome to Utilities',
      body: (
        <p>Utilities are handy everyday tools that sit alongside your finances. New tools will appear here as they're added.</p>
      ),
    },
    {
      target: '[data-tour="utilities-fuel-efficiency"]',
      title: 'Fuel Efficiency',
      body: (
        <p>Track your vehicles' fill-ups to see the <b>mileage</b> you're really getting (km per litre), how much you <b>spend on fuel</b>, and your <b>cost per kilometre</b>.</p>
      ),
    },
    {
      target: '[data-tour="utilities-fastag"]',
      title: 'FASTag',
      body: (
        <p>Keep each vehicle's <b>FASTag balance</b> in one place. Log <b>recharges</b> and <b>toll charges</b>, and get a warning when a tag is running low.</p>
      ),
    },
    {
      title: 'Ready to start?',
      body: (
        <p>Open <b>Fuel Efficiency</b> or <b>FASTag</b> and add your first vehicle or tag. A short guide in each walks you through it.</p>
      ),
      action: { label: 'Open Fuel Efficiency', to: '/utilities/fuel-efficiency' },
    },
  ],

  'fuel-efficiency': [
    {
      title: 'Fuel Efficiency',
      body: (
        <>
          <p>Here's how it works:</p>
          <ul>
            <li>Add each vehicle you own.</li>
            <li>Log every fill-up: odometer reading, amount paid and litres.</li>
            <li>Mickles works out your mileage, total distance and cost per km.</li>
          </ul>
        </>
      ),
    },
    {
      target: '[data-tour="fuel-add-vehicle"]',
      title: 'Add a vehicle',
      body: (
        <>
          <p>Give it a name (for example <b>Honda City</b>), choose <b>Two Wheeler</b>, <b>Four Wheeler</b> or <b>Other</b>, and optionally add the registration number.</p>
          <p>Each registration number can only be used once.</p>
        </>
      ),
    },
    {
      target: '[data-tour="fuel-tabs"]',
      title: 'Active and Archived',
      body: (
        <p>Sold a vehicle or stopped using it? <b>Archive</b> it to move it to the <b>Archived</b> tab. Its fuel history is kept, and you can <b>Restore</b> it any time.</p>
      ),
    },
    {
      target: '[data-tour="fuel-vehicle"]',
      title: 'Your vehicles',
      body: (
        <>
          <p><b>Click a vehicle</b> to open it and log fill-ups.</p>
          <p>Use the <b>⋮</b> menu on a vehicle to edit its details or archive it.</p>
        </>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Add a vehicle, then open it to record your first fill-up. Replay this guide any time with the <b>Guide</b> button.</p>
      ),
    },
  ],

  fastag: [
    {
      title: 'FASTag',
      body: (
        <>
          <p>Here's how it works:</p>
          <ul>
            <li>Add a FASTag for each vehicle with its balance today.</li>
            <li>Log every recharge and toll charge.</li>
            <li>Mickles keeps the balance up to date and warns you when it runs low.</li>
          </ul>
        </>
      ),
    },
    {
      target: '[data-tour="fastag-add"]',
      title: 'Add a FASTag',
      body: (
        <>
          <p>Enter the <b>vehicle number</b> (vehicles from Fuel Efficiency are suggested), the issuer bank and the <b>current balance</b>.</p>
          <p>Set a <b>Low Balance Alert</b> amount. Below it, the tag is flagged.</p>
        </>
      ),
    },
    {
      target: '[data-tour="fastag-summary"]',
      title: 'At a glance',
      body: (
        <p>Your total balance across tags, tolls and recharges this month, and how many tags are low.</p>
      ),
    },
    {
      target: '[data-tour="fastag-tabs"]',
      title: 'Active and Archived',
      body: (
        <p>Sold the vehicle or closed the tag? <b>Archive</b> it. Its history is kept and you can <b>Restore</b> it any time.</p>
      ),
    },
    {
      target: '[data-tour="fastag-card"]',
      title: 'Your FASTags',
      body: (
        <>
          <p><b>Click a tag</b> to log recharges and tolls.</p>
          <p>Use the <b>⋮</b> menu to edit its details or archive it.</p>
        </>
      ),
    },
  ],

  'fastag-details': [
    {
      target: '[data-tour="fastag-actions"]',
      title: 'Recharges and tolls',
      body: (
        <>
          <p><b>Add Recharge</b> when you top up the tag. <b>Add Toll</b> for each toll charge, with the plaza name if you like.</p>
          <p>The balance updates straight away.</p>
        </>
      ),
    },
    {
      target: '[data-tour="fastag-balance"]',
      title: 'Balance and spend',
      body: (
        <p>The current balance (opening balance + recharges − tolls), tolls this month, all tolls and total recharged. The balance turns amber when it drops below your alert.</p>
      ),
    },
    {
      target: '[data-tour="fastag-history"]',
      title: 'History',
      body: (
        <p>Every recharge and toll, newest first. Use the pencil to fix an entry or the bin to delete it.</p>
      ),
    },
  ],

  analysis: [
    {
      title: 'Transaction Analysis',
      body: (
        <>
          <p>See where your money comes from and where it goes, <b>month by month</b> and <b>by category</b>.</p>
          <p>It uses your income and expense transactions. Transfers between your own wallets (like paying a card bill) are left out so nothing is counted twice.</p>
        </>
      ),
    },
    {
      target: '[data-tour="analysis-filters"]',
      title: 'Choose what to look at',
      body: (
        <p>Pick the <b>period</b> (last 12 or 6 months, or a year), a single <b>wallet</b> if you like, and whether the category charts show <b>Expenses</b> or <b>Income</b>. Everything on the page follows these.</p>
      ),
    },
    {
      target: '[data-tour="analysis-summary"]',
      title: 'The headline numbers',
      body: (
        <p>Total income and expenses, how much you saved (or overspent), your savings rate, and your average monthly spend.</p>
      ),
    },
    {
      target: '[data-tour="analysis-monthly"]',
      title: 'Income vs Expenses',
      body: (
        <p>One pair of bars per month. Hover (or tap) a month to see the exact amounts.</p>
      ),
    },
    {
      target: '[data-tour="analysis-categories"]',
      title: 'Categories by month',
      body: (
        <p>Each month's bar split into your biggest categories, so you can spot which one grew. Smaller categories are grouped as <b>Other</b>.</p>
      ),
    },
    {
      target: '[data-tour="analysis-ranking"]',
      title: 'Top categories',
      body: (
        <p>Every category ranked by total, with its share. <b>Click one</b> to see its month-by-month trend and monthly average beside it.</p>
      ),
    },
    {
      target: '[data-tour="analysis-table"]',
      title: 'The numbers',
      body: (
        <p>The same figures as a table: income, expenses, savings and the top category for each month.</p>
      ),
    },
  ],

  'analysis-investments': [
    {
      title: 'Investment Analysis',
      body: (
        <>
          <p>See <b>where your money is invested</b>, how your total has grown, and when you put money in.</p>
          <p>Amounts are what you paid (cost). Current values with live prices are on each investment page.</p>
        </>
      ),
    },
    {
      target: '[data-tour="inv-analysis-filters"]',
      title: 'Period and asset class',
      body: (
        <p>Pick the period (months, a year, or <b>all time by year</b>) and, if you like, one asset class to see its products.</p>
      ),
    },
    {
      target: '[data-tour="inv-analysis-summary"]',
      title: 'The headline numbers',
      body: (
        <p>Total invested, number of holdings, new money put in during the period, your largest holding's share, and realised profit or loss from stock sales.</p>
      ),
    },
    {
      target: '[data-tour="inv-analysis-allocation"]',
      title: 'Where your money is',
      body: (
        <p>Each asset class ranked by amount, with its share. <b>Click one</b> to see the products inside it, then click a product to open it.</p>
      ),
    },
    {
      target: '[data-tour="inv-analysis-growth"]',
      title: 'Total invested over time',
      body: (
        <p>Your running total, built from purchase dates. RDs count each instalment in the month it was paid.</p>
      ),
    },
    {
      target: '[data-tour="inv-analysis-monthly"]',
      title: 'Money put in',
      body: (
        <p>How much you invested each month (or year), split by asset class, so you can see how regular your investing is.</p>
      ),
    },
    {
      target: '[data-tour="inv-analysis-table"]',
      title: 'Holdings by product',
      body: (
        <p>Every product with its amount, share and first purchase date. Click a row to open it.</p>
      ),
    },
  ],

  admin: [
    {
      title: 'Admin',
      body: (
        <>
          <p>Manage <b>users</b> and <b>modules</b> for everyone using Mickles. Only admins see this page.</p>
          <p>Every change you make here is recorded in the <b>Activity Log</b>.</p>
        </>
      ),
    },
    {
      target: '[data-tour="admin-tabs"]',
      title: 'Four areas',
      body: (
        <ul>
          <li><b>Overview</b>: users, sign-ups and how much data each module holds.</li>
          <li><b>Users</b>: search users, make or remove admins, disable or enable accounts, send password reset emails.</li>
          <li><b>Modules</b>: switch parts of the app on or off for everyone.</li>
          <li><b>Activity Log</b>: who did what, and when.</li>
        </ul>
      ),
    },
    {
      target: '[data-tour="admin-summary"]',
      title: 'At a glance',
      body: (
        <p>Total users, new sign-ups and active users in the last 30 days, admins, and disabled accounts.</p>
      ),
    },
    {
      title: 'Privacy',
      body: (
        <p>Admins see each user's email, sign-in dates and <b>how many</b> records they have, never their transactions, balances or other financial details.</p>
      ),
    },
  ],

  insurance: [
    {
      title: 'Welcome to Insurance',
      body: (
        <>
          <p>Keep all your policies in one place: <b>term, life, health, motor, home, travel</b> and more.</p>
          <p>Everything is entered by you. Mickles adds up your cover and premiums and reminds you when a premium or renewal is due.</p>
        </>
      ),
    },
    {
      target: '[data-tour="insurance-add"]',
      title: 'Add a policy',
      body: (
        <>
          <p>Enter the plan name, insurer, cover (sum assured), premium and how often you pay it.</p>
          <p>Add the <b>next due date</b> to get reminders. Policy number, nominee and agent are optional but handy at claim time.</p>
        </>
      ),
    },
    {
      target: '[data-tour="insurance-summary"]',
      title: 'Your cover at a glance',
      body: (
        <p>Active policies, total cover (with life cover shown separately), what you pay in premiums in a year, and premiums due in the next 30 days.</p>
      ),
    },
    {
      target: ['[data-tour="insurance-due"]', '[data-tour="insurance-tabs"]'],
      title: 'Due soon',
      body: (
        <p>Premiums that are overdue or due within 30 days are listed at the top. Click one to open it and record the payment.</p>
      ),
    },
    {
      target: '[data-tour="insurance-tabs"]',
      title: 'Active and inactive',
      body: (
        <p>Policies you mark as <b>lapsed, matured, surrendered or expired</b> move to the Inactive tab, so their history is kept. Search and type filters help when the list grows.</p>
      ),
    },
    {
      target: '[data-tour="insurance-card"]',
      title: 'Open a policy',
      body: (
        <p>Click a policy for its full details and premium history. Use <b>⋮</b> to edit or delete it.</p>
      ),
    },
  ],

  'insurance-details': [
    {
      target: '[data-tour="insurance-actions"]',
      title: 'Record premiums',
      body: (
        <>
          <p>After paying a premium, click <b>Record Premium</b>. The amount is filled in from the policy.</p>
          <p>Keep the tick on, and the <b>next due date moves forward</b> by the premium frequency (for example, a year).</p>
          <p><b>Edit</b> changes any policy detail, including its status.</p>
        </>
      ),
    },
    {
      target: '[data-tour="insurance-tiles"]',
      title: 'Cover and premiums',
      body: (
        <p>The cover, the premium (and what it adds up to in a year), the total premiums you've recorded, and the next due date.</p>
      ),
    },
    {
      target: '[data-tour="insurance-info"]',
      title: 'Policy details',
      body: (
        <p>Who is covered, the nominee, policy number, dates and agent: everything you need when making a claim.</p>
      ),
    },
    {
      target: '[data-tour="insurance-history"]',
      title: 'Premiums paid',
      body: (
        <p>Every premium you've recorded, newest first. Use the pencil to fix one or the bin to delete it.</p>
      ),
    },
  ],

  'vehicle-details': [
    {
      title: 'Logging fill-ups',
      body: (
        <>
          <p>Record each fill-up here and Mickles calculates your vehicle's fuel efficiency.</p>
          <p>For accurate mileage, <b>fill the tank to full</b> whenever you can. Mileage is measured from one full tank to the next.</p>
        </>
      ),
    },
    {
      target: '[data-tour="fuel-add-entry"]',
      title: 'Add a fuel entry',
      body: (
        <>
          <p>Enter the <b>date</b>, the <b>odometer reading</b>, the <b>total amount</b> paid and the <b>litres</b> filled. The price per litre is worked out for you.</p>
          <ul>
            <li>Tick <b>Full Tank</b> if you filled it to the top.</li>
            <li>Your <b>first entry</b> is the starting point and is always a full tank.</li>
            <li>Each new reading must be higher than the last one.</li>
          </ul>
        </>
      ),
    },
    {
      target: '[data-tour="fuel-summary"]',
      title: 'Fuel Efficiency Summary',
      body: (
        <>
          <p>Appears once you have two or more fill-ups:</p>
          <ul>
            <li><b>Avg km/l</b>: your average mileage across full-tank fills.</li>
            <li><b>Total km</b>: distance driven since your first entry.</li>
            <li><b>Cost / km</b>: what each kilometre costs you in fuel.</li>
            <li><b>Total spent</b>: every fill-up added together.</li>
          </ul>
        </>
      ),
    },
    {
      target: '[data-tour="fuel-entries"]',
      title: 'Your fill-ups',
      body: (
        <>
          <p>Every fill-up, newest first. The green <b>km/l</b> badge shows the mileage achieved on that tank.</p>
          <p>Edit or delete entries with the icons on the right. The first entry, marked <b>Baseline</b>, can be edited but not deleted, because every calculation starts from it.</p>
        </>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Log a fill-up each time you visit the pump and watch your averages take shape. Replay this guide any time with the <b>Guide</b> button.</p>
      ),
    },
  ],

  wallets: [
    {
      title: 'Welcome to Wallets',
      body: (
        <>
          <p>Wallets are the places your money lives: <b>bank accounts</b>, <b>credit cards</b> and <b>cash</b>.</p>
          <p>Every transaction you record is linked to a wallet, so set your wallets up first. This quick guide shows you how.</p>
        </>
      ),
    },
    {
      target: '[data-tour="wallets-add"]',
      title: 'Add a wallet',
      body: (
        <>
          <p>Start here. Pick a <b>Wallet Type</b>, give it a name and enter what's in it today.</p>
          <ul>
            <li><b>Bank Account / Cash Wallet</b>: enter the <b>Current Balance</b>.</li>
            <li><b>Credit Card</b>: choose the card network, enter the <b>Credit Limit</b> and the <b>Current Outstanding</b> (what you owe right now).</li>
          </ul>
          <p>Adding the last 4 digits of the account or card number helps you tell wallets apart.</p>
        </>
      ),
    },
    {
      target: '[data-tour="wallets-total"]',
      title: 'Your total balance',
      body: (
        <p>All your bank and cash balances added together, <b>minus what you owe on credit cards</b>. It updates automatically as you record transactions.</p>
      ),
    },
    {
      target: '[data-tour="wallets-types"]',
      title: 'Totals by type',
      body: (
        <p>See how much sits in bank accounts, cash and credit cards. <b>Tap a total</b> to show only that type of wallet, and <b>tap it again</b> to show all.</p>
      ),
    },
    {
      target: '[data-tour="wallets-filters"]',
      title: 'Find and organise',
      body: (
        <p>Search wallets by name, switch between <b>Active</b>, <b>Archived</b> and <b>All</b> wallets, and sort by name, balance or date added.</p>
      ),
    },
    {
      target: '[data-tour="wallets-card"]',
      title: 'Your wallet cards',
      body: (
        <>
          <p>Each card shows the wallet's balance. Credit cards also show the limit, how much is used and how much is available.</p>
          <ul>
            <li><b>Edit</b> (pencil) to change details or correct a balance.</li>
            <li><b>Archive</b> to hide a wallet you no longer use. Its history is kept and you can restore it any time.</li>
            <li><b>Delete</b> only if you added it by mistake.</li>
          </ul>
        </>
      ),
    },
    {
      title: 'Add-on credit cards',
      body: (
        <>
          <p>If two cards share one credit limit (for example an add-on card), add the main card first with <b>Is Primary Card?</b> switched on.</p>
          <p>Then add the second card with it switched off and choose the main card under <b>Link to Primary Card</b>. Both cards will draw from the same limit.</p>
        </>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Once your wallets are in, head to <b>Transactions</b> to record income, expenses and transfers. Balances update automatically. You can replay this guide any time with the <b>Guide</b> button.</p>
      ),
      action: { label: 'Go to Transactions', to: '/transactions' },
    },
  ],

  transactions: [
    {
      title: 'Welcome to Transactions',
      body: (
        <>
          <p>This is where you record every rupee coming in or going out.</p>
          <p>Each transaction is linked to a wallet, and <b>recording it updates that wallet's balance automatically</b>. Make sure you've added your wallets first.</p>
        </>
      ),
    },
    {
      target: '[data-tour="transactions-add"]',
      title: 'Add a transaction',
      body: (
        <>
          <p>Choose the kind of transaction at the top of the form:</p>
          <ul>
            <li><b>Income</b>: salary, interest, refunds. Adds to the wallet.</li>
            <li><b>Expense</b>: anything you spend. Takes from the wallet.</li>
            <li><b>Transfer</b>: moving money between your own wallets, such as paying a credit card bill from your bank. It isn't counted as income or expense.</li>
          </ul>
          <p>Then enter the amount, wallet, category, date and a short description.</p>
        </>
      ),
    },
    {
      target: '[data-tour="transactions-categories"]',
      title: 'Categories',
      body: (
        <p>Create your own income and expense categories, like Groceries, Rent or Salary, each with a colour. Categorising transactions shows you where your money goes.</p>
      ),
    },
    {
      target: '[data-tour="transactions-analysis"]',
      title: 'Analysis',
      body: (
        <p>Charts of your income and expenses <b>month by month</b> and <b>by category</b>, with your savings rate and top spending categories.</p>
      ),
    },
    {
      target: '[data-tour="transactions-summary"]',
      title: 'At a glance',
      body: (
        <p>Total income, total expenses, <b>net flow</b> (income minus expenses) and the number of transactions. These totals follow the filters you apply below.</p>
      ),
    },
    {
      target: '[data-tour="transactions-filters"]',
      title: 'Search and filter',
      body: (
        <>
          <p>The page opens on <b>This month</b>. Pick a <b>Period</b> (Last 7 days, This month, This quarter, This FY from 1 April, All time or Custom dates), then search or narrow down by type, wallet or category.</p>
          <p>Press <b>Update</b> to apply your choices. <b>Reset</b> goes back to this month.</p>
        </>
      ),
    },
    {
      target: '[data-tour="transactions-history"]',
      title: 'Transaction history',
      body: (
        <>
          <p>Every transaction, newest first. Use the <b>pencil</b> to edit or the <b>bin</b> to delete.</p>
          <p>Editing or deleting a transaction corrects the wallet balance for you.</p>
        </>
      ),
    },
    {
      target: '[data-tour="transactions-charts"]',
      title: 'Where your money goes',
      body: (
        <p>Charts break your income and expenses down by category, for whatever you've filtered above.</p>
      ),
    },
    {
      target: '[data-tour="transactions-export"]',
      title: 'Download your data',
      body: (
        <p>Export the transactions you're viewing as a <b>CSV</b> file (for Excel or Google Sheets), a <b>PDF</b>, or an <b>HTML</b> page. Apply filters first to export just a month, a wallet or a category.</p>
      ),
    },
    {
      title: "You're all set",
      body: (
        <p>Record transactions as they happen and your <b>Dashboard</b> will always show where you stand. Replay this guide any time with the <b>Guide</b> button.</p>
      ),
      action: { label: 'Go to Dashboard', to: '/' },
    },
  ],

  // Investments overview, asset classes and product pages (see ./investments/)
  ...INVESTMENT_TOURS,
};
